/**
 * The Export button — `.docs/20` §A: "CSV of the current period's tables, built
 * client-side from the read-model data already on the page (no new action)."
 *
 * Every section of the file is a `DataTable` from `tables.ts`, which is the same
 * object the chart on screen hands to its `details.tableview`. That is the whole
 * reason `tables.ts` exists: an export assembled from the payload a second time can
 * disagree with what the page printed, and nobody would find out until a client did.
 *
 * **It writes each cell's `value`, not its `text`** — session 24, R2-28. The file used
 * to be the page's own rendered strings: `"$3,230"`, `67.8%`, `—` for zero, and
 * `−$890` with a real minus sign (U+2212, `money.ts` `formatWithMinusSign`). Every
 * column landed in Excel as text, nothing summed, and `−$890` would not parse at all.
 * `tables.ts` `Cell` is the fix and its header is the long version; what is here is
 * the two consequences for this file.
 *
 * Four details that are not obvious and are each a bug somewhere else:
 *
 *   - **A BOM.** Titles and text columns still carry `·`, `—` and client names, and
 *     Excel reads a BOM-less file in the system code page. Three bytes buy a file that
 *     opens correctly in the one program this is for.
 *   - **CRLF.** RFC 4180's line ending, and what Excel expects.
 *   - **A leading `=`, `+`, `-` or `@` is quoted with an apostrophe in front of it.**
 *     `client.account_note` is staff-written free text that reaches this file through
 *     `ClientScopeStatusView.action`. A spreadsheet treats a cell starting with any of
 *     those four as a formula; the apostrophe is the standard, boring mitigation, and
 *     it is visible in the cell rather than silently executed.
 *   - **A number is exempt from that**, and it has to be: `-890.00` starts with a `-`,
 *     and defusing it would put the export straight back where it was — a column of
 *     text that will not sum, which is the whole of what R2-28 reported. `NUMERIC` is
 *     what tells the two apart, and it is deliberately strict: a plain optional sign,
 *     digits, an optional decimal part. Nothing with an exponent, a separator, a
 *     leading dot or a space, so nothing a spreadsheet would evaluate as anything but
 *     that number.
 *
 * **The header names each column's unit** — `MRR at month end (USD)`, `Share of
 * August 2026 (fraction of 1)`, `Proposed (ISO date)` — from `DataColumn.unit`. A
 * column of bare numbers with no unit is a column somebody has to guess at, and the
 * screen's own header cannot carry it because the screen prints `$` on every figure.
 */

import type { Formatter } from '../../../format/useFormat.js'
import { saveFile } from '../../../integrations/download.js'
import type { AnalyticsReads } from './data.js'
import { rankRates } from './rates.js'
import { revenueHeading } from './RevenueSection.js'
import {
  clientHealthTable,
  effectiveRateTable,
  mrrMovementsTable,
  mrrSeriesTable,
  packageMixTable,
  pipelineTable,
  revenueMonthsTable,
  revenueShareTable,
  tierMixTable,
  type DataColumn,
  type DataTable,
} from './tables.js'

/** RFC 4180 §2.1. */
const EOL = '\r\n'

/** Byte-order mark, so a spreadsheet reads the file as UTF-8. */
const BOM = '﻿'

/** The characters a spreadsheet treats as the start of a formula. */
const FORMULA_STARTS = ['=', '+', '-', '@'] as const

/**
 * A cell that is a number and nothing else, and is therefore never a formula.
 *
 * Strict on purpose — see the header. `-890.00` and `0` match; `-890.00 ` (trailing
 * space), `1e3`, `1,000`, `.5` and `+1` do not, and each of those goes through the
 * apostrophe like any other text.
 */
const NUMERIC = /^-?\d+(?:\.\d+)?$/

/**
 * `analytics-2026-08.csv` — named for the **guardrail** month the seg is on.
 *
 * Only `analytics_client_health` and `analytics_effective_rate` take a period, and
 * those two sections carry the month in their own titles. The other five are as-of
 * reads: `mrrSeriesTable`, `mrrMovementsTable` and `revenueMonthsTable` name a month
 * in every row, and `revenueShareTable` names the month it answered for. Tier mix,
 * package mix and pipeline have no period at all and are today's picture. The report
 * asks for a period on the five that lack one.
 */
export function csvFilename(period: string): string {
  return `analytics-${period}.csv`
}

/**
 * Every table that has answered, in the order the page prints them.
 *
 * A panel that failed or has not arrived contributes nothing rather than an empty
 * section: the export is what is on the screen.
 */
export function analyticsSections(reads: AnalyticsReads, format: Formatter): readonly DataTable[] {
  const sections: DataTable[] = []

  const mrr = reads.mrr.data
  if (mrr !== undefined) {
    sections.push(mrrSeriesTable(mrr, format), mrrMovementsTable(mrr, format))
  }

  const revenue = reads.revenue.data
  if (revenue !== undefined) {
    sections.push(
      revenueShareTable(revenue, revenueHeading(revenue, format), format),
      revenueMonthsTable(revenue, format),
    )
  }

  const health = reads.health.data
  if (health !== undefined) sections.push(clientHealthTable(health, format))

  const rate = reads.rate.data
  if (rate !== undefined) {
    sections.push(
      effectiveRateTable(
        rate,
        rankRates(rate).rated.map((one) => one.client),
        format,
      ),
    )
  }

  const tierMix = reads.tierMix.data
  if (tierMix !== undefined) sections.push(tierMixTable(tierMix, format))

  const packageMix = reads.packageMix.data
  if (packageMix !== undefined) sections.push(packageMixTable(packageMix, format))

  const pipeline = reads.pipeline.data
  if (pipeline !== undefined) sections.push(pipelineTable(pipeline, format))

  return sections
}

/**
 * The sections as one CSV: a title line, a header row, the rows, and a blank line
 * between sections. Not a single flat table, because seven tables with different
 * columns cannot be one.
 */
export function toCsv(sections: readonly DataTable[]): string {
  return sections
    .map((section) =>
      [
        field(section.title),
        section.columns.map((column) => field(header(column))).join(','),
        ...section.rows.map((row) => row.map((cell) => field(cell.value)).join(',')),
      ].join(EOL),
    )
    .join(`${EOL}${EOL}`)
}

/** `MRR at month end` → `MRR at month end (USD)`. The screen's header keeps its own. */
function header(column: DataColumn): string {
  return column.unit === undefined ? column.label : `${column.label} (${column.unit})`
}

/** One cell: quoted when it has to be, never a formula, and never a defused number. */
function field(value: string): string {
  const safe =
    !NUMERIC.test(value) && FORMULA_STARTS.some((start) => value.startsWith(start))
      ? `'${value}`
      : value
  const mustQuote =
    safe !== value ||
    safe.includes('"') ||
    safe.includes(',') ||
    safe.includes('\n') ||
    safe.includes('\r') ||
    safe !== safe.trim()
  return mustQuote ? `"${safe.replaceAll('"', '""')}"` : safe
}

/**
 * Hands the file to the browser, through the SPA's one download seam
 * (`integrations/download.ts`).
 */
export function downloadCsv(filename: string, text: string): void {
  saveFile(filename, new Blob([`${BOM}${text}`], { type: 'text/csv;charset=utf-8' }))
}
